﻿import type * as CF from '@sa/services/interfaces/customFunction';
import { enqueueCall } from './batch.js';
import { excelToJSDate, flattenExcelRange, validatePositiveNumber } from '$lib/office/excelHelpers.js';
import type { QueryRequest } from '@sa/services/interfaces/queryFunction';
/**
 * This file contains the custom functions for the add-in.
 * All functions must follow the JSDoc format to be exported correctly.
 * https://learn.microsoft.com/en-us/office/dev/add-ins/excel/custom-functions-json-autogeneration#basics-of-jsdoc-tags
 * 
 * FUTURE:  The input validations are quite similar in all of these functions (except for Query).
 *      Consider creating a shared validation utility to reduce code duplication.
 */

/**
 * Connects to your accounting system and looks up the name attached to an account code.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param AccountCode Account code to process.
 * @returns Account Name
 */
export function DESC(
    ScottsOrgId: string,
    AccountCode: string
): Promise<string> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (AccountCode == null || AccountCode == '0' || AccountCode == '') {
        return OfficeExtension.Promise.resolve('');
    }
    const body: CF.DESC = {
        funcName: 'DESC',
        scottsOrgId: ScottsOrgId,
        accountCode: AccountCode
    };
    return enqueueCall(body);
}

/**
 * Connects to your accounting system and sums the GL account balance for an account filtered between two dates.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param Account GL account Name or Code to process.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @returns Sum of accounts
 */
export function GL(
    ScottsOrgId: string,
    Account: string,
    StartDate: number,
    EndDate: number
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    if (Account == null || Account == '0' || Account == '') {
        return OfficeExtension.Promise.reject('Account is missing.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.GL = {
        funcName: 'GL',
        scottsOrgId: ScottsOrgId,
        startAcct: Account,
        startDate: startDateString,
        endDate: endDateString
    };
    return enqueueCall(body);
}

/**
 * Connects to your accounting system and sums the negative GL account balance for an account filtered between two dates.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param Account GL account Name or Code to process.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @returns Negated sum of accounts
 */
export function NGL(
    ScottsOrgId: string,
    Account: string,
    StartDate: number,
    EndDate: number
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    if (Account == null || Account == '0' || Account == '') {
        return OfficeExtension.Promise.reject('Account is missing.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.GL = {
        funcName: 'GL',
        scottsOrgId: ScottsOrgId,
        startAcct: Account,
        startDate: startDateString,
        endDate: endDateString
    };
    return enqueueCall<number>(body).then(sum => (+sum) * -1);
}

/**
 * Connects to your accounting system and sums the GL account balance for an account filtered between two dates.  The result will be signed, where credits will be negative and debits will be positive.
 * //@customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param Account GL account Name or Code to process.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @returns Sum of accounts
 */
export function SGL(
    ScottsOrgId: string,
    Account: string,
    StartDate: number,
    EndDate: number
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    if (Account == null || Account == '0' || Account == '') {
        return OfficeExtension.Promise.reject('Account is missing.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.SGL = {
        funcName: 'SGL',
        scottsOrgId: ScottsOrgId,
        startAcct: Account,
        startDate: startDateString,
        endDate: endDateString
    };
    return enqueueCall(body);
}

/**
 * Connects to your accounting system and sums the GL account balances for a range of account codes filtered between two dates.
 *
 * For Example, calling this function with 200 and 203 will sum the balances of the following account codes: 200, 201, 202 and 203.
 * @customfunction COMRANGE RANGE
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param StartAccountCode First Account Code to process.  This code must be numeric.
 * @param EndAccountCode Last Account Code to process.  This code must be numeric.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @param [IncludeArchived] Optional. Include all accounts in the Range, not just the active ones.
 * @returns Sum of accounts
 */
export function RANGE(
    ScottsOrgId: string,
    StartAccountCode: number,
    EndAccountCode: number,
    StartDate: number,
    EndDate: number,
    IncludeArchived?: boolean
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    if (!validatePositiveNumber(StartAccountCode)) {
        return OfficeExtension.Promise.reject('Start code is not valid.');
    }
    if (!validatePositiveNumber(EndAccountCode)) {
        return OfficeExtension.Promise.reject('End code is not valid.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.RANGE = {
        funcName: 'RANGE',
        scottsOrgId: ScottsOrgId,
        startAcct: StartAccountCode,
        endCode: EndAccountCode,
        startDate: startDateString,
        endDate: endDateString,
        includeAll: IncludeArchived
    };
    return enqueueCall(body);
}

/**
 * Connects to your accounting system and returns the Chart of Accounts for active accounts.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param [IncludeArchived] Optional. Include all accounts in the Chart, not just the active ones.
 * @param [SortOrder] Optional. T for type, D for description.
 * @returns Chart of Accounts
 */
 export function CHART(
    ScottsOrgId: string,
    IncludeArchived?: boolean,
    SortOrder?: string,
): Promise<Array<Array<unknown>>> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    const body: CF.CHART = {
        funcName: 'CHART',
        scottsOrgId: ScottsOrgId,
        includeAll: IncludeArchived,
        sortOrder: SortOrder
    };
    return enqueueCall(body);
}

/**
 * Connects to your accounting system and returns a list of items specified by type
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param Type Type of list you wish to get. (Track, Class, Vendor, etc.)
 * @param [IncludeArchived] Optional. Also return all archived items.
 * @returns Chart of Accounts
 */
export function LIST(
    ScottsOrgId: string,
    Type: string,
    IncludeArchived?: boolean,
): Promise<Array<Array<unknown>>> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    const body: CF.LIST = {
        funcName: 'LIST',
        scottsOrgId: ScottsOrgId,
        type: Type,
        includeAll: IncludeArchived
    };
    return enqueueCall(body);
}

/**
 * Connects to your accounting system and sums the GL account balance for several accounts filtered between two dates.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @param Accounts GL account Names or Codes to process.
 * @returns Sum of accounts
 */
export function GLMULTI(
    ScottsOrgId: string,
    StartDate: number,
    EndDate: number,
    Accounts: string[][][]
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    const accounts = flattenExcelRange(Accounts);
    if (accounts.length === 0) {
        return OfficeExtension.Promise.reject('Accounts are missing.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.GLMULTI = {
        funcName: 'GLMULTI',
        scottsOrgId: ScottsOrgId,
        startDate: startDateString,
        endDate: endDateString,
        accounts: accounts,
    };
    return enqueueCall(body);
}

/**
 * Connects to your accounting system and sums the budgets for an account filtered between two dates.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param BudgetName Budget to process.
 * @param Account Account code or name to process.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @returns Sum of account budgets
 */
 export function BUDGET(
    ScottsOrgId: string,
    BudgetName: string,
    Account: string,
    StartDate: number,
    EndDate: number
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    if (BudgetName == null || BudgetName == '') {
        return OfficeExtension.Promise.reject('Budget is missing.');
    }
    if (Account == null || Account == '0' || Account == '') {
        return OfficeExtension.Promise.reject('Account is missing.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.BUDGET = {
        funcName: 'BUDGET',
        scottsOrgId: ScottsOrgId,
        budgetName: BudgetName,
        startAcct: Account,
        startDate: startDateString,
        endDate: endDateString
    };
    return enqueueCall(body);
}

/**
 * Connects to your accounting system and sums the budgets for a range of account codes filtered between two dates.
 *
 * For Example, calling this function with 200 and 203 will sum the balances of the following account codes: 200, 201, 202 and 203.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param BudgetName Budget to process.
 * @param StartAccountCode First Account Code to process.  This code must be numeric.
 * @param EndAccountCode Last Account Code to process.  This code must be numeric.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @returns Sum of account budgets
 */
 export function BUDGETR(
    ScottsOrgId: string,
    BudgetName: string,
    StartAccountCode: number,
    EndAccountCode: number,
    StartDate: number,
    EndDate: number
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    if (BudgetName == null || BudgetName == '') {
        return OfficeExtension.Promise.reject('Budget is missing.');
    }
    if (!validatePositiveNumber(StartAccountCode)) {
        return OfficeExtension.Promise.reject('Start code is not valid.');
    }
    if (!validatePositiveNumber(EndAccountCode)) {
        return OfficeExtension.Promise.reject('End code is not valid.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.BUDGETR = {
        funcName: 'BUDGETR',
        scottsOrgId: ScottsOrgId,
        budgetName: BudgetName,
        startAcct: StartAccountCode,
        endCode: EndAccountCode,
        startDate: startDateString,
        endDate: endDateString
    };
    return enqueueCall(body);
}

/**
 * Connects to your accounting system and sums the budgets for several accounts filtered between two dates.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param BudgetName Budget to process.
 * @param StartDate Starting date of the range to sum.
 * @param EndDate Final date of the range to sum.
 * @param Accounts GL account Names or Codes to process.
 * @returns Sum of account budgets
 */
 export function BUDGETMULTI(
    ScottsOrgId: string,
    BudgetName: string,
    StartDate: number,
    EndDate: number,
    Accounts: string[][][]
): Promise<number> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    if (!validatePositiveNumber(StartDate)) {
        return OfficeExtension.Promise.reject('Start Date is not valid.');
    }
    if (!validatePositiveNumber(EndDate)) {
        return OfficeExtension.Promise.reject('End Date is not valid.');
    }
    if (StartDate > EndDate) {
        return OfficeExtension.Promise.reject('Start Date cannot be after the End Date.');
    }
    if (BudgetName == null || BudgetName == '') {
        return OfficeExtension.Promise.reject('Budget is missing.');
    }
    const accounts = flattenExcelRange(Accounts);
    if (accounts.length === 0) {
        return OfficeExtension.Promise.reject('Accounts are missing.');
    }
    const startDateString = excelToJSDate(StartDate).toISOString();
    const endDateString = excelToJSDate(EndDate).toISOString();
    const body: CF.BUDGETMULTI = {
        funcName: 'BUDGETMULTI',
        scottsOrgId: ScottsOrgId,
        budgetName: BudgetName,
        startDate: startDateString,
        endDate: endDateString,
        accounts: accounts,
    };
    return enqueueCall(body);
}

/**
 * Connects to your accounting system and runs a custom query defined in JSON.
 * @customfunction
 * @helpurl https://scottsaddins.com/faq
 * @param ScottsOrgId Scotts Organization ID.
 * @param Query JSON string representing the query.
 * @returns an array of the query results.
 */
export function QUERY(
    ScottsOrgId: string,
    Query: string,
): Promise<Array<Array<unknown>>> {
    if (ScottsOrgId == null || ScottsOrgId == '0' || ScottsOrgId == '') {
        return OfficeExtension.Promise.reject('ScottsOrgId is missing.');
    }
    // Validate that Query is a valid JSON string
    let parsedQuery: QueryRequest;
    try {
        parsedQuery = JSON.parse(Query) as QueryRequest;
    } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Invalid JSON';
        console.error('QUERY: Failed to parse queryJSON:', errorMsg, Query);
        return OfficeExtension.Promise.reject(`Invalid JSON in query: ${errorMsg}`);
    }

    // Validate QueryRequest structure
    if (typeof parsedQuery.scottsOrgId !== 'number') {
        return OfficeExtension.Promise.reject('Query must contain a valid scottsOrgId (number)');
    }
    if (typeof parsedQuery.dataType !== 'string') {
        return OfficeExtension.Promise.reject('Query must contain a valid dataType (string)');
    }
    if (!Array.isArray(parsedQuery.columns)) {
        return OfficeExtension.Promise.reject('Query must contain a valid columns (array)');
    }
    if (!Array.isArray(parsedQuery.filters)) {
        return OfficeExtension.Promise.reject('Query must contain a valid filters (array)');
    }

    const body: CF.QUERY = {
        funcName: 'QUERY',
        scottsOrgId: ScottsOrgId,
        queryJSON: Query,
    };
    return enqueueCall(body);
}
